import { defineComponent as _defineComponent } from 'vue'
import { PropType, onMounted, ref, computed } from 'vue';

import { FormItem } from '@/interfaces/lib/model';
import {
  SdsSection3Cas,
  SdsSection3Material,
} from '@/interfaces/model/general/sds';
import { useParentMaterials } from '@/composables/useParentMaterials';
import { generateTempMaterialId } from '@/utils/uuid';
import {
  getConcentrationInequalityList,
  concentrationUnitList,
  textFormList,
} from '@/lib/formItem';

import {
  isFloat,
  isNumberAndAlphabetsAndHyphenArray,
  required,
  requiredSelection,
} from '@/lib/rule';
import { VForm } from 'vuetify/lib/components';
import { i18n } from '@/plugins/vue-i18n';
import sharedMessages from '@/lib/shared-messages';


export default /*#__PURE__*/_defineComponent({
  __name: 'SdsSection3MaterialForm',
  props: {
  isEditing: {
    type: Boolean,
    required: true,
  },
  isValid: {
    type: Boolean,
    required: true,
  },
  sdsId: {
    type: String,
    required: true,
  },
  sdsSection3Id: {
    type: Number,
    required: true,
  },
  sdsSection3MaterialId: {
    type: Number,
    default: 0,
  },
  materialName: {
    type: String,
    required: true,
  },
  casList: {
    type: Array as PropType<Array<SdsSection3Cas>>,
    required: true,
  },
  encsNumber: {
    type: String,
    required: true,
  },
  ishlNumber: {
    type: String,
    required: true,
  },
  isStated: {
    type: Boolean,
    required: true,
  },
  concentration: {
    type: String,
    required: true,
  },
  concentrationMin: {
    type: Number,
    required: false,
    default: null,
  },
  concentrationMax: {
    type: Number,
    required: false,
    default: null,
  },
  concentrationInequalitySign: {
    type: Number,
    required: true,
  },
  concentrationUnit: {
    type: String,
    required: true,
  },
  // Parent material props
  parentMaterialId: {
    type: Number,
    default: null,
  },
  tempMaterialId: {
    type: String,
    default: undefined,
  },
  tempParentMaterialId: {
    type: String,
    default: null,
  },
  allMaterials: {
    type: Array as PropType<SdsSection3Material[]>,
    required: true,
  },
},
  emits: ["update:isValid", "update:materialName", "update:casList", "update:encsNumber", "update:ishlNumber", "update:isStated", "update:concentrationInequalitySign", "update:concentrationMin", "update:concentrationMax", "update:concentrationUnit", "update:parentMaterialId", "update:tempMaterialId", "update:tempParentMaterialId"],
  setup(__props, { emit: emits }: { emit: ({
  (e: 'update:isValid', data: boolean): void;
  (e: 'update:materialName', data: string): void;
  (e: 'update:casList', data: Array<SdsSection3Cas>): void;
  (e: 'update:encsNumber', data: string): void;
  (e: 'update:ishlNumber', data: string): void;
  (e: 'update:isStated', data: boolean): void;
  (e: 'update:concentrationInequalitySign', data: number): void;
  (e: 'update:concentrationMin', data: number | null): void;
  (e: 'update:concentrationMax', data: number | null): void;
  (e: 'update:concentrationUnit', data: string): void;
  // Parent material emits
  (e: 'update:parentMaterialId', data: number | null): void;
  (e: 'update:tempMaterialId', data: string): void;
  (e: 'update:tempParentMaterialId', data: string | null): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props;

const $t = i18n.t.bind(i18n);



const isMaterialConcentrationSecret = ref<boolean>(false);
const isNotStated = ref<boolean>(!props.isStated);
const form = ref<InstanceType<typeof VForm> | null>(null);



// Parent materials composable
const materialsRef = computed(() => props.allMaterials);
const { getAvailableParents } = useParentMaterials(materialsRef);

// Parent material reactive state
const hasParentMaterial = ref<boolean>(false);
const selectedParentId = ref<number | string | null>(null);

// Available parents for dropdown
const availableParents = computed(() => {
  const currentMaterial: SdsSection3Material = {
    id: props.sdsSection3MaterialId || 0,
    temp_material_id: props.tempMaterialId,
    sds_id: props.sdsId,
    sds_section3_id: props.sdsSection3Id,
    material_name: props.materialName,
    concentration: props.concentration,
    encs_number: props.encsNumber,
    ishl_number: props.ishlNumber,
    is_stated: props.isStated,
    concentration_inequality_sign: props.concentrationInequalitySign,
    concentration_min: props.concentrationMin,
    concentration_max: props.concentrationMax,
    concentration_unit: props.concentrationUnit,
    cas_list: props.casList,
    created_at: '',
    updated_at: '',
    deleted_at: null,
  };
  return getAvailableParents(currentMaterial);
});

// Helper function for v-select item-value
const getParentItemValue = (item: {
  id?: number;
  temp_material_id?: string;
}) => {
  return item.id || item.temp_material_id;
};

// Form event handlers
const onParentCheckboxChange = (checked: boolean) => {
  if (!checked) {
    selectedParentId.value = null;
    emits('update:parentMaterialId', null);
    emits('update:tempParentMaterialId', null);
  }
};

const onParentSelectionChange = (value: number | string | null) => {
  if (typeof value === 'number') {
    // Existing parent material
    emits('update:parentMaterialId', value);
    emits('update:tempParentMaterialId', null);
  } else if (typeof value === 'string') {
    // New parent material (temp UUID)
    emits('update:parentMaterialId', null);
    emits('update:tempParentMaterialId', value);
  } else {
    // Cleared selection
    emits('update:parentMaterialId', null);
    emits('update:tempParentMaterialId', null);
  }
};

const bindCas = (casList: Array<SdsSection3Cas>): Array<string> => {
  return casList.map((x: SdsSection3Cas) => {
    return x.cas_number;
  });
};

const showInequalityMin = (concentrationInequalitySign: number): boolean => {
  switch (concentrationInequalitySign) {
    case 1:
      return true;
    case 2:
      return true;
    case 3:
      return true;
    case 4:
      return false;
    case 5:
      return false;
    case 6:
      return true;
    case 7:
      return true;
    case 8:
      return true;
    case 9:
      return true;
    default:
      return false;
  }
};

const showInequalityMax = (concentrationInequalitySign: number): boolean => {
  switch (concentrationInequalitySign) {
    case 1:
      return false;
    case 2:
      return false;
    case 3:
      return false;
    case 4:
      return true;
    case 5:
      return true;
    case 6:
      return true;
    case 7:
      return true;
    case 8:
      return true;
    case 9:
      return true;
    default:
      return false;
  }
};

const inequalityMin = (concentrationInequalitySign: number): string => {
  switch (concentrationInequalitySign) {
    case 1:
      return $t(sharedMessages.equal);
    case 2:
      return $t(sharedMessages.greaterThanOrEqual);
    case 3:
      return $t(sharedMessages.greaterThan);
    case 4:
      return '';
    case 5:
      return '';
    case 6:
      return $t(sharedMessages.greaterThanOrEqual);
    case 7:
      return $t(sharedMessages.greaterThanOrEqual);
    case 8:
      return $t(sharedMessages.greaterThan);
    case 9:
      return $t(sharedMessages.greaterThan);
    default:
      return '';
  }
};

const inequalityMax = (concentrationInequalitySign: number): string => {
  switch (concentrationInequalitySign) {
    case 1:
      return '';
    case 2:
      return '';
    case 3:
      return '';
    case 4:
      return $t(sharedMessages.lessThanOrEqual);
    case 5:
      return $t(sharedMessages.lessThan);
    case 6:
      return $t(sharedMessages.lessThanOrEqual);
    case 7:
      return $t(sharedMessages.lessThan);
    case 8:
      return $t(sharedMessages.lessThanOrEqual);
    case 9:
      return $t(sharedMessages.lessThan);
    default:
      return '';
  }
};

const encsNumberValue = (encsNumber: string): Array<string> => {
  return encsNumber.split('|').filter((x) => x != '');
};

const ishlNumberValue = (ishlNumber: string): Array<string> => {
  return ishlNumber.split('|').filter((x) => x != '');
};

const updateIsValid = (e: boolean): void => {
  emits('update:isValid', e);
};

const updateisStated = (e: boolean): void => {
  emits('update:isStated', !e);
};

const updateMaterialName = (e: string): void => {
  emits('update:materialName', e);
};

const updateEncsNumber = (e: Array<string | FormItem>): void => {
  const encsList = e.map((item) => {
    if (typeof item == 'string') {
      return item;
    } else {
      return String(item.value);
    }
  });

  emits('update:encsNumber', encsList.join('|'));
};

const updateIshlNumber = (e: Array<string | FormItem>): void => {
  const ishlList = e.map((item) => {
    if (typeof item == 'string') {
      return item;
    } else {
      return String(item.value);
    }
  });

  emits('update:ishlNumber', ishlList.join('|'));
};

const updateConcentrationMin = (e: string | null): void => {
  let concentrationMin: number | null = null;
  if (e) {
    concentrationMin = Number(e);
  }
  emits('update:concentrationMin', concentrationMin);
};

const updateConcentrationMax = (e: string | null): void => {
  let concentrationMax: number | null = null;
  if (e) {
    concentrationMax = Number(e);
  }
  emits('update:concentrationMax', concentrationMax);
};

const updateConcentrationUnit = (e: null | string | FormItem): void => {
  emits('update:concentrationUnit', e?.value ?? e ?? '');
};

const updateConcentrationInequalitySign = (e: number): void => {
  emits('update:concentrationInequalitySign', e);
};

const updateCas = (e: Array<string | FormItem>) => {
  const casList: Array<SdsSection3Cas> = e.map((i: string | FormItem) => {
    return {
      id: 0,
      cas_number: i?.value ?? i,
      sds_id: props.sdsId,
      sds_section3_material_id: props.sdsSection3Id,
      created_at: '',
      updated_at: '',
      deleted_at: null,
    };
  });

  emits('update:casList', casList);
};

const updateConcentrationSecret = (e: boolean): void => {
  if (e) {
    updateConcentrationMin(null);
    updateConcentrationMax(null);
    updateConcentrationUnit('');
    updateConcentrationInequalitySign(0);
  } else {
    updateConcentrationMin(0);
    updateConcentrationMax(0);
    updateConcentrationUnit('%');
    updateConcentrationInequalitySign(1);
  }
};

onMounted((): void => {
  if (form.value) {
    form.value.validate();
  }
  if (props.concentrationInequalitySign == 0) {
    isMaterialConcentrationSecret.value = true;
  } else {
    isMaterialConcentrationSecret.value = false;
  }

  // Initialize parent material state
  // Generate temp_material_id for new materials if not provided
  if (
    !props.tempMaterialId &&
    (!props.sdsSection3Id || props.sdsSection3Id === 0)
  ) {
    emits('update:tempMaterialId', generateTempMaterialId());
  }

  // Initialize parent material selection state
  const hasParent = !!(props.parentMaterialId || props.tempParentMaterialId);
  hasParentMaterial.value = hasParent;
  selectedParentId.value =
    props.parentMaterialId || props.tempParentMaterialId || null;
});

return { __sfc: true,$t, props, isMaterialConcentrationSecret, isNotStated, form, emits, materialsRef, getAvailableParents, hasParentMaterial, selectedParentId, availableParents, getParentItemValue, onParentCheckboxChange, onParentSelectionChange, bindCas, showInequalityMin, showInequalityMax, inequalityMin, inequalityMax, encsNumberValue, ishlNumberValue, updateIsValid, updateisStated, updateMaterialName, updateEncsNumber, updateIshlNumber, updateConcentrationMin, updateConcentrationMax, updateConcentrationUnit, updateConcentrationInequalitySign, updateCas, updateConcentrationSecret, getConcentrationInequalityList, concentrationUnitList, textFormList, isFloat, isNumberAndAlphabetsAndHyphenArray, required, requiredSelection, VForm, sharedMessages }
}

})